/**
 * Okta Authentication - Web Implementation
 * Uses @okta/okta-auth-js for browser-based authentication
 */
import OktaAuth from "@okta/okta-auth-js";
import { tokenStorage } from "../storage";
import { signInWithProxy, verifyMFAWithProxy, selectMFAFactorWithProxy, setOktaProxyConfig, } from "./okta.proxy";
import { extractUserInfoFromToken } from "../utils/jwt";
let oktaAuthInstance = null;
/**
 * Initialize Okta Auth client
 */
export function initOktaAuth(config) {
    // Configure proxy usage based on config
    setOktaProxyConfig(config.useProxy ?? false); // Default to false to match auth.config.ts
    const oktaConfig = {
        issuer: config.issuer,
        pkce: true,
        tokenManager: {
            storage: "sessionStorage",
            autoRenew: true,
        },
        cookies: {
            secure: typeof window !== "undefined" && window.location.protocol === "https:",
            sameSite: "lax",
        },
    };
    let currentUrlParams = new URLSearchParams(window.location.search);
    console.log("currentUrlParams:", currentUrlParams.toString());
    oktaAuthInstance = new OktaAuth(oktaConfig);
    return oktaAuthInstance;
}
/**
 * Get the Okta Auth instance
 */
export function getOktaAuth() {
    if (!oktaAuthInstance) {
        throw new Error("OktaAuth not initialized. Call initOktaAuth first.");
    }
    return oktaAuthInstance;
}
/**
 * Initiate login using redirect to Okta hosted login
 * This avoids CORS issues with direct API calls
 */
export async function signInWithRedirect() {
    const oktaAuth = getOktaAuth();
    console.log("🔑 Initiating redirect to Okta hosted login...");
    await oktaAuth.token.getWithRedirect({
        scopes: oktaAuth.options.scopes,
    });
}
/**
 * Sign in with username and password
 * Uses proxy implementation to avoid CORS issues
 */
export async function signIn(credentials) {
    console.log("🔑 Okta signIn called with:", {
        username: credentials.username,
    });
    try {
        // Use proxy implementation to avoid CORS
        console.log("🔑 Using proxy implementation...");
        return await signInWithProxy(credentials);
    }
    catch (error) {
        throw error;
    }
}
/**
 * Verify MFA factor
 * Uses proxy implementation to avoid CORS issues
 */
export async function verifyMFA(request) {
    try {
        // Use proxy implementation to avoid CORS
        return await verifyMFAWithProxy(request);
    }
    catch (error) {
        console.error("MFA verification failed:", error);
        throw error;
    }
}
/**
 * Select MFA factor and send challenge
 * Uses proxy implementation to avoid CORS issues
 */
export async function selectMFAFactor(stateToken, factorId) {
    try {
        // Use proxy implementation to avoid CORS
        return await selectMFAFactorWithProxy(stateToken, factorId);
    }
    catch (error) {
        console.error("Select MFA factor failed:", error);
        throw error;
    }
}
/**
 * Sign out user
 */
export async function signOut() {
    const oktaAuth = getOktaAuth();
    try {
        await oktaAuth.signOut();
        await tokenStorage.clearTokens();
    }
    catch (error) {
        console.error("Sign out failed:", error);
        // Clear tokens even if Okta signOut fails
        await tokenStorage.clearTokens();
        throw error;
    }
}
/**
 * Get current user info
 */
export async function getUserInfo() {
    console.log("🔑 [getUserInfo] Getting user info...");
    try {
        // First, check sessionStorage for user info (set during proxy login)
        const storedUserInfo = sessionStorage.getItem("okta_user_info");
        if (storedUserInfo) {
            try {
                const userInfo = JSON.parse(storedUserInfo);
                console.log("🔑 [getUserInfo] Retrieved user info from sessionStorage");
                return userInfo;
            }
            catch (e) {
                console.warn("🔑 [getUserInfo] Failed to parse stored user info:", e);
            }
        }
        // Try to get from tokenStorage (sessionToken stored there)
        const tokens = await tokenStorage.getTokens();
        console.log("🔑 [getUserInfo] Tokens from storage:", {
            hasAccessToken: !!tokens?.accessToken,
            hasIdToken: !!tokens?.idToken
        });
        if (tokens?.idToken) {
            // Try to decode ID token to get user info (avoids CORS issues)
            const userInfo = extractUserInfoFromToken(tokens.idToken);
            if (userInfo) {
                console.log("🔑 [getUserInfo] Extracted user info from ID token");
                return userInfo;
            }
        }
        if (tokens?.accessToken) {
            // Fallback: try access token
            const userInfo = extractUserInfoFromToken(tokens.accessToken);
            if (userInfo) {
                console.log("🔑 [getUserInfo] Extracted user info from access token");
                return userInfo;
            }
        }
        console.warn("🔑 [getUserInfo] Could not extract user info from tokens, trying Okta...");
        // Last resort: Try Okta SDK (may fail with CORS in proxy mode)
        const oktaAuth = getOktaAuth();
        const userInfo = await oktaAuth.getUser();
        console.log("🔑 [getUserInfo] Got user info from Okta SDK");
        return {
            sub: userInfo.sub,
            email: userInfo.email,
            emailVerified: userInfo.email_verified,
            givenName: userInfo.given_name,
            familyName: userInfo.family_name,
            name: userInfo.name,
            preferredUsername: userInfo.preferred_username,
            groups: userInfo.groups,
        };
    }
    catch (error) {
        console.error("🔑 [getUserInfo] All methods failed:", error);
        // If all else fails, return minimal user info
        const tokens = await tokenStorage.getTokens();
        if (tokens?.accessToken) {
            // Create minimal user from token
            return {
                sub: "user",
                email: "user@example.com",
            };
        }
        return null;
    }
}
/**
 * Refresh tokens
 */
export async function refreshTokens() {
    const oktaAuth = getOktaAuth();
    try {
        const accessToken = await oktaAuth.tokenManager.renew("accessToken");
        const idToken = await oktaAuth.tokenManager.get("idToken");
        if (accessToken && idToken) {
            const tokens = {
                accessToken: accessToken.accessToken,
                idToken: idToken.idToken,
                expiresAt: accessToken.expiresAt ??
                    Math.floor(Date.now() / 1000) + 3600,
                tokenType: "Bearer",
            };
            await tokenStorage.setTokens(tokens);
            return tokens;
        }
        return null;
    }
    catch (error) {
        console.error("Token refresh failed:", error);
        return null;
    }
}
/**
 * Check if user is authenticated
 */
export async function isAuthenticated() {
    const oktaAuth = getOktaAuth();
    return oktaAuth.isAuthenticated();
}
/**
 * Initiate forgot password flow
 */
export async function forgotPassword(request) {
    const oktaAuth = getOktaAuth();
    try {
        const transaction = (await oktaAuth.forgotPassword({
            username: request.username,
            factorType: "EMAIL",
        }));
        if (transaction.status === "RECOVERY_CHALLENGE") {
            return { success: true, stateToken: transaction.data?.stateToken };
        }
        return { success: false };
    }
    catch (error) {
        console.error("Forgot password failed:", error);
        throw error;
    }
}
/**
 * Handle OAuth callback
 */
export async function handleCallback() {
    const oktaAuth = getOktaAuth();
    try {
        const tokenResponse = await oktaAuth.token.parseFromUrl();
        if (tokenResponse.tokens) {
            const tokens = extractTokens(tokenResponse);
            await tokenStorage.setTokens(tokens);
            return tokens;
        }
        return null;
    }
    catch (error) {
        console.error("Handle callback failed:", error);
        throw error;
    }
}
function extractTokens(tokenResponse) {
    const { tokens } = tokenResponse;
    const accessToken = tokens.accessToken;
    const idToken = tokens.idToken;
    if (!accessToken || !idToken) {
        throw new Error("Missing required tokens in response");
    }
    return {
        accessToken: accessToken.accessToken,
        idToken: idToken.idToken,
        refreshToken: tokens.refreshToken?.refreshToken,
        expiresAt: accessToken.expiresAt ?? Math.floor(Date.now() / 1000) + 3600,
        tokenType: "Bearer",
    };
}
//# sourceMappingURL=okta.web.js.map